import { BoardPresence, userColor } from "../../shared/boards";
import { byId } from "../dom";

// a ring with a name for everyone else on a shared board: where their pen is, and a pulse while it is down.
// they come from the live feed, fade when someone stops moving and go when they leave

interface Cursor {
  el: HTMLElement;
  ring: HTMLElement;
  last: BoardPresence;
  idle: number;
  gone: number;
}

const cursors = new Map<string, Cursor>();
let toScreen: (x: number, y: number) => [number, number] = (x, y) => [x, y];
let scale: () => number = () => 1;

const FADE_MS = 3500;
const DROP_MS = 12_000;

export function setupPresence(project: (x: number, y: number) => [number, number], zoom: () => number): void {
  toScreen = project;
  scale = zoom;
}

function place(cursor: Cursor): void {
  const [x, y] = toScreen(cursor.last.x, cursor.last.y);
  cursor.el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
  // as wide as their brush looks on this screen, but never so small it can't be found
  const diameter = Math.min(90, Math.max(16, cursor.last.size * scale()));
  cursor.ring.style.width = `${diameter}px`;
  cursor.ring.style.height = `${diameter}px`;
  cursor.el.style.setProperty("--r", `${diameter / 2}px`);
}

export function showPresence(event: BoardPresence): void {
  const host = byId("wb-cursors");
  if (event.gone) {
    removeCursor(event.session);
    return;
  }
  let cursor = cursors.get(event.session);
  if (!cursor) {
    const el = document.createElement("div");
    el.className = "wb-cursor";
    const color = userColor(event.by);
    el.style.setProperty("--who", color);
    const ring = document.createElement("i");
    const name = document.createElement("em");
    name.textContent = event.name;
    el.append(ring, name);
    host.append(el);
    cursor = { el, ring, last: event, idle: 0, gone: 0 };
    cursors.set(event.session, cursor);
  }
  cursor.last = event;
  cursor.ring.style.setProperty("--ink", event.color);
  cursor.el.classList.toggle("down", event.down);
  cursor.el.classList.remove("idle");
  place(cursor);
  window.clearTimeout(cursor.idle);
  window.clearTimeout(cursor.gone);
  cursor.idle = window.setTimeout(() => cursor?.el.classList.add("idle"), FADE_MS);
  cursor.gone = window.setTimeout(() => removeCursor(event.session), DROP_MS);
}

function removeCursor(session: string): void {
  const cursor = cursors.get(session);
  if (!cursor) return;
  window.clearTimeout(cursor.idle);
  window.clearTimeout(cursor.gone);
  cursor.el.remove();
  cursors.delete(session);
}

// the board moved under them, so every ring moves with it
export function movePresence(): void {
  cursors.forEach(place);
}

export function clearPresence(): void {
  [...cursors.keys()].forEach(removeCursor);
}
